import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';

import { CurrentUserModule } from '®shared/current-user';
import { TarlV3ApiModule } from '®shared/tarl-v3-api';

import {
  EVENT_SEND_INTERVAL_MS,
  EVENT_SERVICE_MAX_BATCH_SIZE,
  EVENT_SERVICE_MAX_RETRY_COUNT,
  EVENT_SERVICE_SEND_ENABLED,
  EventsModuleConfig,
} from './events-config.constant';
import { EventsService } from './events.service';

const TEST_EVENTS_MODULE_CONFIG: EventsModuleConfig = {
  eventSendIntervalMilliseconds: 5000,
  isSendEnabled: false,
  maxBatchSize: 5,
  maxRetryCount: 3,
};

@NgModule({
  imports: [CommonModule, CurrentUserModule, TarlV3ApiModule],
})
export class EventsModule {
  /**
   * The EventsModule should contain shared singletons.
   *
   * The module is configurable, to allow the event-service to be run without automated sending.
   *
   * @see {@link https://angular-2-training-book.rangle.io/handout/modules/shared-di-tree.html}
   * @return {ModuleWithProviders<EventsModule>} EventsModule with EventsService singleton
   */
  static forRoot(): ModuleWithProviders<EventsModule> {
    return {
      ngModule: EventsModule,
      providers: [
        { provide: EVENT_SEND_INTERVAL_MS, useValue: 5000 },
        { provide: EVENT_SERVICE_MAX_BATCH_SIZE, useValue: 100 },
        { provide: EVENT_SERVICE_MAX_RETRY_COUNT, useValue: 3 },
        { provide: EVENT_SERVICE_SEND_ENABLED, useValue: true },
        EventsService,
      ],
    };
  }

  /**
   * The EventsModule should contain shared singletons, but be configurable for testing purposes.
   *
   * Originally, we wanted to have a single `forRoot` with default configuration, which could be
   * called with a specific configuration to customise.  However, that solution did not work with
   * ahead-of-time compilation (obtuse errors about disallowing "functions" in the build).
   *
   * The main reason this exists is so that we can disable automatic collection and sending of events.
   *
   * @see {@link https://angular-2-training-book.rangle.io/handout/modules/shared-di-tree.html}
   * @param {EventsModuleConfig} config A configuration for the events module
   * @return {ModuleWithProviders<EventsModule>} EventsModule with EventsService singleton
   */
  static forTestRoot(
    config?: EventsModuleConfig,
  ): ModuleWithProviders<EventsModule> {
    const configOrDefault = config ? config : TEST_EVENTS_MODULE_CONFIG;
    const {
      eventSendIntervalMilliseconds,
      isSendEnabled,
      maxBatchSize,
      maxRetryCount,
    } = configOrDefault;

    return {
      ngModule: EventsModule,
      providers: [
        {
          provide: EVENT_SEND_INTERVAL_MS,
          useValue: eventSendIntervalMilliseconds,
        },
        { provide: EVENT_SERVICE_MAX_BATCH_SIZE, useValue: maxBatchSize },
        { provide: EVENT_SERVICE_MAX_RETRY_COUNT, useValue: maxRetryCount },
        { provide: EVENT_SERVICE_SEND_ENABLED, useValue: isSendEnabled },
        EventsService,
      ],
    };
  }
}
